import { api } from "@/lib/apiClient";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";

export type SaleProductPick = {
  id: string;
  name: string;
  salePrice: number | null;
  inventoryStatus: string;
  thumbnailUrl: string | null;
};

export const SALE_PRODUCT_SEARCH_MIN_CHARS = 2;
export const SALE_PRODUCT_SEARCH_DEBOUNCE_MS = 400;
export const SALE_PRODUCT_SEARCH_PAGE_SIZE = 32;

type SaleProductSearchMeta = {
  page: number;
  last_page: number;
  total: number;
};

function mapRow(raw: Record<string, unknown>): SaleProductPick | null {
  const id = raw.id;
  if (id === undefined || id === null || String(id).trim() === "") return null;
  const saleRaw = raw.sale_price ?? raw.salePrice;
  const salePrice =
    saleRaw === null || saleRaw === undefined || saleRaw === ""
      ? null
      : Number.isFinite(Number(saleRaw))
        ? Number(saleRaw)
        : null;
  const statusRaw = raw.inventory_status ?? raw.inventoryStatus ?? "draft";

  return {
    id: String(id),
    name: String(raw.name ?? ""),
    salePrice,
    inventoryStatus: String(statusRaw),
    thumbnailUrl: resolveProductThumbnailUrl(raw),
  };
}

/** Server-side product search for sale forms (lightweight picker mode). */
export async function searchSaleProducts(
  query: string,
  page = 1,
  options?: { inventoryStatus?: string },
): Promise<{ data: SaleProductPick[]; meta: SaleProductSearchMeta }> {
  const search = query.trim();
  const response = await api.get("/api/v1/catalog/products", {
    params: {
      picker: 1,
      page: Math.max(1, page),
      per_page: SALE_PRODUCT_SEARCH_PAGE_SIZE,
      search: search || undefined,
      inventory_status: options?.inventoryStatus ?? "in_stock",
      sort: "name:asc",
    },
  });
  const body = response.data as Record<string, unknown>;
  const rows = Array.isArray(body.data) ? (body.data as Record<string, unknown>[]) : [];
  const metaRaw = (body.meta ?? {}) as Record<string, unknown>;
  const data = rows.map(mapRow).filter((r): r is SaleProductPick => r !== null);

  return {
    data,
    meta: {
      page: Math.max(1, Number(metaRaw.current_page ?? metaRaw.page ?? page)),
      last_page: Math.max(1, Number(metaRaw.last_page ?? 1)),
      total: Math.max(0, Number(metaRaw.total ?? data.length)),
    },
  };
}

export function saleProductPickFromApiBody(raw: unknown): SaleProductPick | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const nested = o.data;
  if (nested && typeof nested === "object") {
    return mapRow(nested as Record<string, unknown>);
  }
  return mapRow(o);
}
